import { reactive } from 'vue';
import ClientelingTasksAPI from 'dashboard/api/clientelingTasks';
import { mockTaskProjects } from './story/fixtures.js';

const clone = value => JSON.parse(JSON.stringify(value));

const normalizeContact = contact => {
  if (!contact?.id) return null;
  return {
    id: String(contact.id),
    name:
      contact.name ||
      contact.email ||
      contact.phoneNumber ||
      contact.phone ||
      '',
    email: contact.email ?? null,
    phone: contact.phone ?? contact.phoneNumber ?? null,
    segment: contact.segment ?? contact.additionalAttributes?.segment ?? null,
    assignedSellerId: contact.assignedSellerId ?? contact.assigneeId ?? null,
  };
};

const normalizeProject = (project, fallbackContactId = null) => {
  if (!project?.id) return null;
  return {
    id: String(project.id),
    contactId: String(
      project.contactId ?? project.customerId ?? fallbackContactId ?? ''
    ),
    title: project.title ?? '',
    status: project.status ?? 'active',
    budget: project.budget ?? null,
    currency: project.currency ?? 'EUR',
    productsCount: project.productsCount ?? project.items?.length ?? 0,
    createdAt:
      typeof project.createdAt === 'number'
        ? new Date(project.createdAt * 1000).toISOString()
        : (project.createdAt ?? new Date().toISOString()),
  };
};

const state = reactive({
  tasks: [],
  contacts: [],
  projects: clone(mockTaskProjects),
  sellers: [],
  isLoading: false,
  isLoaded: false,
  error: null,
});

const upsertById = (collection, item) => {
  if (!item?.id) return;
  const index = collection.findIndex(existing => existing.id === item.id);
  if (index === -1) collection.unshift(item);
  else collection[index] = { ...collection[index], ...item };
};

const activity = (type, label, actor) => ({
  id: `activity_${Date.now()}_${Math.random().toString(16).slice(2)}`,
  type,
  label,
  createdAt: new Date().toISOString(),
  actor: actor ?? state.sellers[0],
});

const normalizeTask = task => {
  if (!task?.id) return null;
  return {
    ...task,
    id: String(task.id),
    assignedTo: task.assignedTo
      ? {
          id: String(task.assignedTo.id),
          name: task.assignedTo.name,
        }
      : null,
    linkedContactId:
      task.linkedContactId === null || task.linkedContactId === undefined
        ? null
        : String(task.linkedContactId),
    linkedCloseCircleMemberId:
      task.linkedCloseCircleMemberId === null ||
      task.linkedCloseCircleMemberId === undefined
        ? null
        : String(task.linkedCloseCircleMemberId),
    linkedConversationId:
      task.linkedConversationId === null ||
      task.linkedConversationId === undefined
        ? null
        : String(task.linkedConversationId),
    dueTimePresent: task.dueTimePresent ?? true,
  };
};

const normalizeSeller = seller => {
  if (!seller?.id) return null;
  return {
    id: String(seller.id),
    name: seller.name,
  };
};

const taskPayloadToApi = payload => ({
  title: payload.title,
  description: payload.description ?? '',
  status: payload.status ?? 'todo',
  priority: payload.priority ?? 'medium',
  task_type: payload.type || null,
  due_at: payload.dueDate,
  due_time_present: payload.dueTimePresent ?? true,
  assigned_user_id: payload.assignedToId ?? payload.assignedTo?.id,
  contact_id: payload.linkedContactId,
  linked_project_id: payload.linkedProjectId,
  close_circle_member_id: payload.linkedCloseCircleMemberId,
  linked_conversation_id: payload.linkedConversationId,
  linked_product_id: payload.linkedProductId,
  reminder_at: payload.reminder?.enabled ? payload.reminder.date : null,
});

export const useTasksStore = () => {
  const applyApiResponse = data => {
    if (Array.isArray(data.contacts)) {
      state.contacts = data.contacts.map(normalizeContact).filter(Boolean);
    }
    if (Array.isArray(data.sellers)) {
      state.sellers = data.sellers.map(normalizeSeller).filter(Boolean);
    }
    if (Array.isArray(data.tasks)) {
      state.tasks = data.tasks.map(normalizeTask).filter(Boolean);
    }
  };

  const loadTasks = async ({ force = false, filters = {} } = {}) => {
    if (state.isLoaded && !force) return state.tasks;
    state.isLoading = true;
    state.error = null;
    try {
      const { data } = await ClientelingTasksAPI.get(filters);
      applyApiResponse(data);
      state.isLoaded = true;
      return state.tasks;
    } catch (error) {
      state.error = error;
      throw error;
    } finally {
      state.isLoading = false;
    }
  };

  const findContact = id =>
    state.contacts.find(contact => String(contact.id) === String(id));

  const findProject = id =>
    state.projects.find(project => String(project.id) === String(id));

  const projectsForContact = contactId =>
    state.projects.filter(
      project => String(project.contactId) === String(contactId)
    );

  const tasksForContact = contactId =>
    state.tasks.filter(
      task => String(task.linkedContactId) === String(contactId)
    );

  const tasksForProject = projectId =>
    state.tasks.filter(
      task => String(task.linkedProjectId) === String(projectId)
    );

  const registerContact = contact => {
    const normalized = normalizeContact(contact);
    upsertById(state.contacts, normalized);
    return normalized;
  };

  const registerProject = (project, fallbackContactId = null) => {
    const normalized = normalizeProject(project, fallbackContactId);
    upsertById(state.projects, normalized);
    return normalized;
  };

  const updateTaskLocally = (taskId, updates, activityType = 'updated') => {
    const index = state.tasks.findIndex(
      task => String(task.id) === String(taskId)
    );
    if (index === -1) return null;
    const { activityLabel, ...taskUpdates } = updates;
    const actor = updates.assignedTo ?? state.tasks[index].assignedTo;
    state.tasks[index] = {
      ...state.tasks[index],
      ...taskUpdates,
      updatedAt: updates.updatedAt ?? new Date().toISOString(),
      activity: [
        activity(activityType, activityLabel ?? 'Suivi mis à jour', actor),
        ...(state.tasks[index].activity ?? []),
      ],
    };
    return state.tasks[index];
  };

  const createTaskLocally = payload => {
    const now = new Date().toISOString();
    const assignedTo =
      state.sellers.find(seller => seller.id === payload.assignedToId) ??
      payload.assignedTo ??
      state.sellers[0];
    const project = payload.linkedProjectId
      ? findProject(payload.linkedProjectId)
      : null;
    const task = {
      id: `task_${Date.now()}`,
      title: payload.title,
      description: payload.description ?? '',
      status: payload.status ?? 'todo',
      priority: payload.priority ?? 'medium',
      type: payload.type ?? null,
      dueDate: payload.dueDate,
      dueTimePresent: payload.dueTimePresent ?? true,
      createdAt: now,
      updatedAt: now,
      assignedTo,
      linkedContactId: payload.linkedContactId ?? project?.contactId ?? null,
      linkedProjectId: payload.linkedProjectId ?? null,
      linkedCloseCircleMemberId: payload.linkedCloseCircleMemberId ?? null,
      closeCircleMemberName: payload.closeCircleMemberName ?? '',
      linkedConversationId: payload.linkedConversationId ?? null,
      linkedProductId: payload.linkedProductId ?? null,
      reminder: payload.reminder ?? {
        enabled: false,
        date: null,
      },
      activity: [activity('created', 'Suivi créé', assignedTo)],
    };
    state.tasks.unshift(task);
    return task;
  };

  const upsertTask = task => {
    if (!task?.id) return null;
    const normalized = normalizeTask(task);
    const index = state.tasks.findIndex(
      existing => String(existing.id) === String(normalized.id)
    );
    if (index === -1) state.tasks.unshift(normalized);
    else state.tasks[index] = { ...state.tasks[index], ...normalized };
    return normalized;
  };

  const saveTask = async payload => {
    const apiPayload = taskPayloadToApi(payload);
    const response = payload.id
      ? await ClientelingTasksAPI.update(payload.id, apiPayload)
      : await ClientelingTasksAPI.create(apiPayload);
    if (response.data.contact) registerContact(response.data.contact);
    return upsertTask(response.data.task ?? response.data);
  };

  const createTask = async payload => saveTask(payload);

  const updateTask = async (taskId, updates) =>
    saveTask({
      ...state.tasks.find(task => String(task.id) === String(taskId)),
      ...updates,
      id: taskId,
    });

  const completeTask = async taskId =>
    updateTask(taskId, {
      status: 'done',
      activityLabel: 'Suivi terminé',
    });

  const postponeTask = async (taskId, dueDate) =>
    updateTask(taskId, {
      dueDate,
      activityLabel: 'Échéance reportée',
    });

  const deleteTask = async taskId => {
    await ClientelingTasksAPI.delete(taskId);
    const index = state.tasks.findIndex(task => task.id === taskId);
    if (index !== -1) state.tasks.splice(index, 1);
  };

  return {
    state,
    loadTasks,
    findContact,
    findProject,
    projectsForContact,
    tasksForContact,
    tasksForProject,
    registerContact,
    registerProject,
    saveTask,
    createTask,
    updateTask,
    completeTask,
    postponeTask,
    deleteTask,
    createTaskLocally,
    updateTaskLocally,
  };
};
